import { 
  PaymentTransaction, 
  PaymentTransactionType, 
  generateTransactionId,
  EnhancedPaymentReceivedEvent,
  EnhancedPaymentSentEvent
} from '@/types/paymentTransaction';
import { Bill } from '@/types';
import { billEventService } from './billEventService';
import { BillEventType } from '@/types/billEvent';
import { billService } from './billService';

export const paymentTransactionService = {
  /**
   * Create a new payment transaction (incoming)
   */
  createPaymentReceived: async (
    tripId: string,
    billId: string,
    amount: number,
    personId?: string,
    personName?: string,
    reference?: string,
    paymentMethod?: string,
    description?: string,
    createdBy?: string,
    createdByName?: string
  ): Promise<string> => {
    // Get current bill to calculate totals
    const currentBill = await billService.getBill(tripId, billId);
    if (!currentBill) {
      throw new Error('Bill not found');
    }

    const transactionId = generateTransactionId();
    const now = new Date();
    
    // Create payment transaction record
    const transaction: PaymentTransaction = {
      id: transactionId,
      type: PaymentTransactionType.RECEIVED,
      amount,
      personId,
      personName,
      reference,
      paymentMethod,
      description,
      timestamp: now,
      billEventId: '', // Will be set after creating the event
      createdBy: createdBy || 'unknown',
      createdByName,
      createdAt: now,
      updatedAt: now,
    };

    // Calculate new totals
    const newPaidAmount = currentBill.paidAmount + amount;
    const remainingAmount = currentBill.grandTotal - newPaidAmount;

    // Create enhanced payment received event
    const paymentEvent = billEventService.createPaymentReceivedEvent(
      amount,
      newPaidAmount,
      remainingAmount,
      createdBy,
      createdByName,
      paymentMethod,
      reference
    ) as EnhancedPaymentReceivedEvent;

    // Enhance the event with transaction details
    paymentEvent.details = {
      ...paymentEvent.details,
      personId,
      personName,
      description,
      transactionId,
    };

    // Update transaction with event ID
    transaction.billEventId = paymentEvent.id;

    // Update the bill with new transaction and event
    const updatedTransactions = [...currentBill.paymentTransactions, transaction];
    const updatedEvents = [...currentBill.events, paymentEvent];

    // Update bill status based on payment
    const isFullyPaid = newPaidAmount >= currentBill.grandTotal;
    const updateData: Partial<Bill> = {
      paidAmount: newPaidAmount,
      isPaid: isFullyPaid,
      paymentTransactions: updatedTransactions,
      events: updatedEvents,
    };

    if (isFullyPaid) {
      updateData.status = 'fully_paid';
      updateData.paidAt = now;
    } else if (newPaidAmount > 0) {
      updateData.status = 'partially_paid';
    }

    await billService.updateBill(tripId, billId, updateData);
    return transactionId;
  },

  /**
   * Create a new payment transaction (outgoing)
   */
  createPaymentSent: async (
    tripId: string,
    billId: string,
    amount: number,
    recipient?: string,
    personId?: string,
    personName?: string,
    reference?: string,
    paymentMethod?: string,
    description?: string,
    createdBy?: string,
    createdByName?: string
  ): Promise<string> => {
    // Get current bill
    const currentBill = await billService.getBill(tripId, billId);
    if (!currentBill) {
      throw new Error('Bill not found');
    }

    const transactionId = generateTransactionId();
    const now = new Date();
    
    // Create payment transaction record
    const transaction: PaymentTransaction = {
      id: transactionId,
      type: PaymentTransactionType.SENT,
      amount,
      personId,
      personName,
      reference,
      paymentMethod,
      description,
      timestamp: now,
      billEventId: '', // Will be set after creating the event
      createdBy: createdBy || 'unknown',
      createdByName,
      createdAt: now,
      updatedAt: now,
    };

    // Create enhanced payment sent event
    const paymentEvent = billEventService.createPaymentSentEvent(
      amount,
      createdBy,
      createdByName,
      recipient,
      paymentMethod,
      reference
    ) as EnhancedPaymentSentEvent;

    // Enhance the event with transaction details
    paymentEvent.details = {
      ...paymentEvent.details,
      personId,
      personName,
      description,
      transactionId,
    };

    // Update transaction with event ID
    transaction.billEventId = paymentEvent.id;

    // Update the bill with new transaction and event
    const updatedTransactions = [...currentBill.paymentTransactions, transaction];
    const updatedEvents = [...currentBill.events, paymentEvent];

    const updateData = {
      paymentTransactions: updatedTransactions,
      events: updatedEvents,
    };

    await billService.updateBill(tripId, billId, updateData);
    return transactionId;
  },

  /**
   * Update an existing payment transaction
   */
  updatePaymentTransaction: async (
    tripId: string,
    billId: string,
    transactionId: string,
    updates: Partial<Pick<PaymentTransaction, 'amount' | 'personId' | 'personName' | 'reference' | 'paymentMethod' | 'description'>>
  ): Promise<void> => {
    const currentBill = await billService.getBill(tripId, billId);
    if (!currentBill) {
      throw new Error('Bill not found');
    }

    const transactionIndex = currentBill.paymentTransactions.findIndex(t => t.id === transactionId);
    if (transactionIndex === -1) {
      throw new Error('Transaction not found');
    }

    // Update the transaction
    const updatedTransactions = [...currentBill.paymentTransactions];
    updatedTransactions[transactionIndex] = {
      ...updatedTransactions[transactionIndex],
      ...updates,
      updatedAt: new Date(),
    };

    // If amount changed, recalculate bill totals
    const updateData: Partial<Bill> = {
      paymentTransactions: updatedTransactions,
    };

    if (updates.amount !== undefined) {
      const newPaidAmount = updatedTransactions
        .filter(t => t.type === PaymentTransactionType.RECEIVED)
        .reduce((sum, t) => sum + t.amount, 0);
      
      const isFullyPaid = newPaidAmount >= currentBill.grandTotal;
      updateData.paidAmount = newPaidAmount;
      updateData.isPaid = isFullyPaid;
      
      if (isFullyPaid && !currentBill.paidAt) {
        updateData.paidAt = new Date();
        updateData.status = 'fully_paid';
      } else if (newPaidAmount > 0 && newPaidAmount < currentBill.grandTotal) {
        updateData.status = 'partially_paid';
      } else if (newPaidAmount === 0) {
        updateData.status = currentBill.sentAt ? 'sent' : 'generated';
        updateData.paidAt = null;
      }
    }

    await billService.updateBill(tripId, billId, updateData);
  },

  /**
   * Delete a payment transaction
   */
  deletePaymentTransaction: async (
    tripId: string,
    billId: string,
    transactionId: string
  ): Promise<void> => {
    const currentBill = await billService.getBill(tripId, billId);
    if (!currentBill) {
      throw new Error('Bill not found');
    }

    // Remove the transaction
    const updatedTransactions = currentBill.paymentTransactions.filter(t => t.id !== transactionId);
    
    // Recalculate bill totals
    const newPaidAmount = updatedTransactions
      .filter(t => t.type === PaymentTransactionType.RECEIVED)
      .reduce((sum, t) => sum + t.amount, 0);
    
    const isFullyPaid = newPaidAmount >= currentBill.grandTotal;
    
    const updateData = {
      paymentTransactions: updatedTransactions,
      paidAmount: newPaidAmount,
      isPaid: isFullyPaid,
      status: isFullyPaid ? 'fully_paid' : 
              (newPaidAmount > 0 ? 'partially_paid' : 
               (currentBill.sentAt ? 'sent' : 'generated')),
      paidAt: isFullyPaid ? (currentBill.paidAt || new Date()) : null,
    };

    await billService.updateBill(tripId, billId, updateData);
  },

  /**
   * Get all payment transactions for a bill
   */
  getPaymentTransactions: (bill: { paymentTransactions: PaymentTransaction[] }): PaymentTransaction[] => {
    return [...bill.paymentTransactions].sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime());
  },

  /**
   * Get payment transactions by type
   */
  getPaymentTransactionsByType: (
    bill: { paymentTransactions: PaymentTransaction[] }, 
    type: PaymentTransactionType
  ): PaymentTransaction[] => {
    return bill.paymentTransactions
      .filter(t => t.type === type)
      .sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime());
  },

  /**
   * Calculate total received payments
   */
  getTotalReceived: (bill: { paymentTransactions: PaymentTransaction[] }): number => {
    return bill.paymentTransactions
      .filter(t => t.type === PaymentTransactionType.RECEIVED)
      .reduce((sum, t) => sum + t.amount, 0);
  },

  /**
   * Calculate total sent payments  
   */
  getTotalSent: (bill: { paymentTransactions: PaymentTransaction[] }): number => {
    return bill.paymentTransactions
      .filter(t => t.type === PaymentTransactionType.SENT)
      .reduce((sum, t) => sum + t.amount, 0);
  },
};